import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../api/services/authService';

// Create Auth Context
const AuthContext = createContext();

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const userData = localStorage.getItem('user_data');

        // If we have both token and userData, restore the session
        // (remember_me only affects persistence across browser restarts, not page refreshes)
        if (token && userData) {
          try {
            const parsedUserData = JSON.parse(userData);

            // Basic token validation - check if token has proper structure
            if (token.length > 20) { // JWT tokens are longer than 20 chars
              setUser(parsedUserData);
              setIsAuthenticated(true);
            } else {
              // Invalid token structure, clear data
              localStorage.removeItem('auth_token');
              localStorage.removeItem('user_data');
              setUser(null);
              setIsAuthenticated(false);
            }
          } catch (parseError) {
            // Clear invalid data
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user_data');
            setUser(null);
            setIsAuthenticated(false);
          }
        } else {
          // No token or userData - user is not logged in
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        // Clear invalid tokens
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Login function
  const login = async (email, password, rememberMe = false) => {
    try {
      // Login attempt initiated
      const response = await authService.login(email, password, rememberMe);
      if (response.success) {
        setUser(response.user);
        setIsAuthenticated(true);

        // Store token and user data - use consistent keys
        localStorage.setItem('auth_token', response.token);
        localStorage.setItem('user_data', JSON.stringify(response.user));

        // Store remember_me preference
        if (rememberMe) {
          localStorage.setItem('remember_me', 'true');
        } else {
          // Clear remember_me if user didn't check it
          localStorage.removeItem('remember_me');
        }
        return { success: true };
      } else {
        return {
          success: false,
          error: response?.error || 'Login failed'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error?.message || 'Network error. Please check your connection.'
      };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Logout initiated

      // Call logout API first (with token still available)
      await authService.logout();
    } catch (error) {
    } finally {
      // Always clear local state and storage regardless of API call result
      setUser(null);
      setIsAuthenticated(false);

      // Clear auth data but preserve remember_me preference
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      // Don't clear remember_me flag - preserve user preference for next session
    }
  };

  // Get current user function
  const getCurrentUser = async () => {
    try {
      const userData = authService.getCurrentUser();
      if (userData) {
        setUser(userData);
        return { success: true, data: userData };
      } else {
        return { success: false, error: 'No user data found' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Login with OTP function
  const loginWithOTP = async (userData) => {
    try {
      if (userData) {
        setUser(userData);
        setIsAuthenticated(true);

        // User data is already stored in localStorage by authService
        return { success: true };
      } else {
        return { success: false, error: 'No user data provided' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Verify OTP function
  const verifyOTP = async (email, otp) => {
    try {
      const response = await authService.verifyOTP({ emailOrPhone: email, otp });
      return response;
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Update user function
  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user_data', JSON.stringify(userData));
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    loginWithOTP,
    logout,
    verifyOTP,
    getCurrentUser,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
export default AuthContext;
